import React, { useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import './ConfirmDialog.css';

/**
 * A yes/no question before a destructive action. Escape and a click outside cancel (not while `busy`); Cancel has the
 * focus when it opens, so Enter never deletes by accident; `busy` disables both buttons so the action runs once.
 */
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Delete', busyText = 'Working…', busy = false }) => {
  const titleId = useId();
  const cancelRef = useRef(null);
  useEffect(() => {
    if (!isOpen) { return undefined; }
    cancelRef.current?.focus();
    const onKey = (event) => { if (event.key === 'Escape' && !busy) { onCancel(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, busy, onCancel]);
  if (!isOpen) return null;

  return (
    <div className="confirm-dialog-overlay" onClick={busy ? undefined : onCancel}>
      <div aria-labelledby={titleId} aria-modal="true" className="confirm-dialog" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="confirm-dialog-content">
          <h3 className="confirm-dialog-title" id={titleId}>{title}</h3>
          <p className="confirm-dialog-message">{message}</p>
        </div>
        <div className="confirm-dialog-actions">
          <button
            className="confirm-dialog-button confirm-dialog-button-cancel"
            disabled={busy}
            onClick={onCancel}
            ref={cancelRef}
            type="button"
          >
            {'Cancel'}
          </button>
          <button
            className="confirm-dialog-button confirm-dialog-button-confirm"
            disabled={busy}
            onClick={onConfirm}
            type="button"
          >
            {busy ? busyText : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  busy: PropTypes.bool,
  busyText: PropTypes.string,
  confirmText: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  message: PropTypes.node.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

export default ConfirmDialog;
